// src/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/realEstateOwnersData.js

/**
 * Get current language from localStorage
 */
function getCurrentLanguage() {
  return localStorage.getItem("lang") || "en";
}

/**
 * Format month display based on language
 */
function formatMonthDisplay(date) {
  const lang = getCurrentLanguage();
  return date.toLocaleDateString(lang === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "short"
  });
}

// Get language once at module load
const lang = getCurrentLanguage();
const isGerman = lang === "de";

export const realEstateData = {
  // Building Metadata
  building: {
    id: 1,
    name: "TechHub München",
    location: "Munich, Germany",
    totalArea: 10000, // sqm
    floorsCount: 4,
    floorArea: 2500, // sqm per floor
    capacity: 200, // people
    operatingHours: {
      start: "08:00",
      end: "19:00",
      weekdays: isGerman
        ? ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"]
        : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
    },
    benchmarks: {
      energyIntensity: 70, // kWh/sqm
      co2Emissions: 70, // % target
      esgScore: 70, // % target
      occupancyRate: 75 // % target
    },
    energyCostPerKwh: 0.28, // € (German commercial rate 2025)
    co2FactorPerKwh: 0.3 // kg CO₂/kWh (German electricity mix)
  },

  // Historical Monthly Data (Jan 2024 - Oct 2025)
  monthlyData: [
    {
      month: "2024-01",
      displayMonth: formatMonthDisplay(new Date(2024, 0, 31)),
      date: "2024-01-31",

      energy: {
        intensity: 52.0,
        changePercent: null,
        totalKwh: 520000,
        totalCost: 145600,
        nextMonthPrediction: 142800,
        topAppliancesShare: 72.5,
        comparison: { previous: 48.0, current: 52.0 },
        activeSpaces: {
          openOffice: 65,
          meetingRoom: 20,
          lobby: 10,
          conferenceRoom: 5
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [28, 32, 38, 42, 45, 48, 52, 58, 45, 35, 25, 18],
          gas: [15, 18, 22, 12, 20, 22, 15, 18, 15, 28, 32, 30]
        }
      },
      carbon: {
        totalEmissions: 1560,
        changePercent: null,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.5,
            changePercent: null,
            emissions: 133
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 24.0,
            changePercent: null,
            emissions: 374
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.5,
            changePercent: null,
            emissions: 460
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.0,
            changePercent: null,
            emissions: 593
          }
        ],
        gaugeValue: 78,
        timeline: { current: 1560, projected: 1580 }
      },
      esg: {
        overallScore: 78,
        changePercent: null,
        gaugeValue: 78,
        spaceAllocation: {
          lobby: 48,
          conferenceRoom: 12,
          meetingRoom: 16,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 72,
        changePercent: null,
        peopleOnsite: 144,
        totalCapacity: 200,
        donutData: { occupied: 72, free: 28 },
        floorplanBreakdown: {
          openOffice: 66,
          meetingRoom: 19,
          lobby: 10,
          conferenceRoom: 5
        },
        spaceUtilization: 48
      },
      operational: {
        energyAnalytics: 68,
        changePercent: null,
        dynamicOccupancy: 72,
        utilizationAnalytics: 72,
        spaceEfficiency: {
          openOffice: 32,
          meetingRoom: 20,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 30, red: 19, yellow: 16 }
      }
    },
    {
      month: "2024-02",
      displayMonth: formatMonthDisplay(new Date(2024, 1, 29)),
      date: "2024-02-29",
      energy: {
        intensity: 51.0,
        changePercent: -1.92,
        totalKwh: 510000,
        totalCost: 142800,
        nextMonthPrediction: 136080,
        topAppliancesShare: 71.8,
        comparison: { previous: 52.0, current: 51.0 },
        activeSpaces: {
          openOffice: 66,
          meetingRoom: 19,
          lobby: 10,
          conferenceRoom: 5
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [27, 31, 37, 41, 44, 47, 51, 57, 44, 34, 24, 17],
          gas: [14, 17, 21, 11, 19, 21, 14, 17, 14, 27, 31, 29]
        }
      },
      carbon: {
        totalEmissions: 1530,
        changePercent: -1.92,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.8,
            changePercent: 3.5,
            emissions: 135
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 24.2,
            changePercent: 0.8,
            emissions: 370
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.3,
            changePercent: -0.7,
            emissions: 448
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 37.7,
            changePercent: -0.8,
            emissions: 577
          }
        ],
        gaugeValue: 77,
        timeline: { current: 1530, projected: 1550 }
      },
      esg: {
        overallScore: 79,
        changePercent: 1.28,
        gaugeValue: 79,
        spaceAllocation: {
          lobby: 49,
          conferenceRoom: 11,
          meetingRoom: 16,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 74,
        changePercent: 2.78,
        peopleOnsite: 148,
        totalCapacity: 200,
        donutData: { occupied: 74, free: 26 },
        floorplanBreakdown: {
          openOffice: 67,
          meetingRoom: 19,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 50
      },
      operational: {
        energyAnalytics: 69,
        changePercent: 1.47,
        dynamicOccupancy: 74,
        utilizationAnalytics: 74,
        spaceEfficiency: {
          openOffice: 32,
          meetingRoom: 19,
          lobby: 39,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 31, red: 20, yellow: 17 }
      }
    },
    {
      month: "2024-03",
      displayMonth: formatMonthDisplay(new Date(2024, 2, 31)),
      date: "2024-03-31",
      energy: {
        intensity: 48.5,
        changePercent: -4.9,
        totalKwh: 485000,
        totalCost: 135800,
        nextMonthPrediction: 123200,
        topAppliancesShare: 70.2,
        comparison: { previous: 51.0, current: 48.5 },
        activeSpaces: {
          openOffice: 66,
          meetingRoom: 19,
          lobby: 11,
          conferenceRoom: 4
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [25, 29, 35, 39, 42, 45, 48, 54, 42, 32, 22, 15],
          gas: [12, 15, 18, 9, 16, 18, 12, 15, 12, 24, 28, 26]
        }
      },
      carbon: {
        totalEmissions: 1455,
        changePercent: -4.9,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 9.0,
            changePercent: 2.3,
            emissions: 131
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 24.5,
            changePercent: 1.2,
            emissions: 356
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.0,
            changePercent: -1.0,
            emissions: 422
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 37.5,
            changePercent: -0.5,
            emissions: 546
          }
        ],
        gaugeValue: 75,
        timeline: { current: 1455, projected: 1470 }
      },
      esg: {
        overallScore: 80,
        changePercent: 1.27,
        gaugeValue: 80,
        spaceAllocation: {
          lobby: 49,
          conferenceRoom: 11,
          meetingRoom: 16,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 76,
        changePercent: 2.7,
        peopleOnsite: 152,
        totalCapacity: 200,
        donutData: { occupied: 76, free: 24 },
        floorplanBreakdown: {
          openOffice: 67,
          meetingRoom: 18,
          lobby: 11,
          conferenceRoom: 4
        },
        spaceUtilization: 52
      },
      operational: {
        energyAnalytics: 71,
        changePercent: 2.9,
        dynamicOccupancy: 76,
        utilizationAnalytics: 76,
        spaceEfficiency: {
          openOffice: 33,
          meetingRoom: 19,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 32, red: 21, yellow: 18 }
      }
    },
    {
      month: "2024-04",
      displayMonth: formatMonthDisplay(new Date(2024, 3, 30)),
      date: "2024-04-30",
      energy: {
        intensity: 44.0,
        changePercent: -9.28,
        totalKwh: 440000,
        totalCost: 123200,
        nextMonthPrediction: 117600,
        topAppliancesShare: 68.5,
        comparison: { previous: 48.5, current: 44.0 },
        activeSpaces: {
          openOffice: 67,
          meetingRoom: 19,
          lobby: 10,
          conferenceRoom: 4
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [22, 26, 32, 35, 38, 41, 44, 48, 38, 28, 18, 12],
          gas: [8, 10, 13, 6, 12, 14, 9, 11, 9, 18, 22, 20]
        }
      },
      carbon: {
        totalEmissions: 1320,
        changePercent: -9.28,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 9.5,
            changePercent: 5.6,
            emissions: 125
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 25.0,
            changePercent: 2.0,
            emissions: 330
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 28.5,
            changePercent: -1.7,
            emissions: 376
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 37.0,
            changePercent: -1.3,
            emissions: 488
          }
        ],
        gaugeValue: 72,
        timeline: { current: 1320, projected: 1410 }
      },
      esg: {
        overallScore: 81,
        changePercent: 1.25,
        gaugeValue: 81,
        spaceAllocation: {
          lobby: 50,
          conferenceRoom: 11,
          meetingRoom: 15,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 78,
        changePercent: 2.63,
        peopleOnsite: 156,
        totalCapacity: 200,
        donutData: { occupied: 78, free: 22 },
        floorplanBreakdown: {
          openOffice: 68,
          meetingRoom: 18,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 54
      },
      operational: {
        energyAnalytics: 74,
        changePercent: 4.23,
        dynamicOccupancy: 78,
        utilizationAnalytics: 78,
        spaceEfficiency: {
          openOffice: 33,
          meetingRoom: 19,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 33, red: 22, yellow: 19 }
      }
    },
    {
      month: "2024-05",
      displayMonth: formatMonthDisplay(new Date(2024, 4, 31)),
      date: "2024-05-31",
      energy: {
        intensity: 42.0,
        changePercent: -4.55,
        totalKwh: 420000,
        totalCost: 117600,
        nextMonthPrediction: 128800,
        topAppliancesShare: 67.0,
        comparison: { previous: 44.0, current: 42.0 },
        activeSpaces: {
          openOffice: 67,
          meetingRoom: 18,
          lobby: 11,
          conferenceRoom: 4
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [20, 24, 30, 33, 36, 39, 42, 46, 36, 26, 16, 10],
          gas: [6, 8, 10, 4, 9, 11, 7, 9, 7, 15, 18, 16]
        }
      },
      carbon: {
        totalEmissions: 1260,
        changePercent: -4.55,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 10.0,
            changePercent: 5.3,
            emissions: 126
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 25.5,
            changePercent: 2.0,
            emissions: 321
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 28.0,
            changePercent: -1.8,
            emissions: 353
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 36.5,
            changePercent: -1.4,
            emissions: 460
          }
        ],
        gaugeValue: 70,
        timeline: { current: 1260, projected: 1380 }
      },
      esg: {
        overallScore: 82,
        changePercent: 1.23,
        gaugeValue: 82,
        spaceAllocation: {
          lobby: 50,
          conferenceRoom: 10,
          meetingRoom: 16,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 79,
        changePercent: 1.28,
        peopleOnsite: 158,
        totalCapacity: 200,
        donutData: { occupied: 79, free: 21 },
        floorplanBreakdown: {
          openOffice: 68,
          meetingRoom: 18,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 55
      },
      operational: {
        energyAnalytics: 76,
        changePercent: 2.7,
        dynamicOccupancy: 79,
        utilizationAnalytics: 79,
        spaceEfficiency: {
          openOffice: 34,
          meetingRoom: 18,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 34, red: 23, yellow: 20 }
      }
    },
    {
      month: "2024-06",
      displayMonth: formatMonthDisplay(new Date(2024, 5, 30)),
      date: "2024-06-30",
      energy: {
        intensity: 46.0,
        changePercent: 9.52,
        totalKwh: 460000,
        totalCost: 128800,
        nextMonthPrediction: 151200,
        topAppliancesShare: 69.5,
        comparison: { previous: 42.0, current: 46.0 },
        activeSpaces: {
          openOffice: 68,
          meetingRoom: 18,
          lobby: 10,
          conferenceRoom: 4
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [24, 28, 34, 38, 42, 46, 50, 56, 44, 32, 20, 12],
          gas: [4, 5, 6, 3, 5, 6, 4, 5, 4, 8, 10, 9]
        }
      },
      carbon: {
        totalEmissions: 1380,
        changePercent: 9.52,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 10.5,
            changePercent: 5.0,
            emissions: 145
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 26.0,
            changePercent: 2.0,
            emissions: 359
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 27.5,
            changePercent: -1.8,
            emissions: 380
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 36.0,
            changePercent: -1.4,
            emissions: 497
          }
        ],
        gaugeValue: 73,
        timeline: { current: 1380, projected: 1575 }
      },
      esg: {
        overallScore: 81,
        changePercent: -1.22,
        gaugeValue: 81,
        spaceAllocation: {
          lobby: 50,
          conferenceRoom: 11,
          meetingRoom: 15,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 77,
        changePercent: -2.53,
        peopleOnsite: 154,
        totalCapacity: 200,
        donutData: { occupied: 77, free: 23 },
        floorplanBreakdown: {
          openOffice: 67,
          meetingRoom: 19,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 53
      },
      operational: {
        energyAnalytics: 73,
        changePercent: -3.95,
        dynamicOccupancy: 77,
        utilizationAnalytics: 77,
        spaceEfficiency: {
          openOffice: 33,
          meetingRoom: 19,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 33, red: 22, yellow: 19 }
      }
    },
    {
      month: "2024-07",
      displayMonth: formatMonthDisplay(new Date(2024, 6, 31)),
      date: "2024-07-31",
      energy: {
        intensity: 54.0,
        changePercent: 17.39,
        totalKwh: 540000,
        totalCost: 151200,
        nextMonthPrediction: 148400,
        topAppliancesShare: 74.5,
        comparison: { previous: 46.0, current: 54.0 },
        activeSpaces: {
          openOffice: 69,
          meetingRoom: 18,
          lobby: 9,
          conferenceRoom: 4
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [32, 38, 45, 50, 55, 60, 65, 70, 58, 45, 30, 20],
          gas: [3, 4, 5, 2, 4, 5, 3, 4, 3, 6, 8, 7]
        }
      },
      carbon: {
        totalEmissions: 1620,
        changePercent: 17.39,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 11.5,
            changePercent: 9.5,
            emissions: 186
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 27.0,
            changePercent: 3.8,
            emissions: 437
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 27.0,
            changePercent: -1.8,
            emissions: 437
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 34.5,
            changePercent: -4.2,
            emissions: 559
          }
        ],
        gaugeValue: 81,
        timeline: { current: 1620, projected: 1605 }
      },
      esg: {
        overallScore: 79,
        changePercent: -2.47,
        gaugeValue: 79,
        spaceAllocation: {
          lobby: 49,
          conferenceRoom: 11,
          meetingRoom: 16,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 68,
        changePercent: -11.69,
        peopleOnsite: 136,
        totalCapacity: 200,
        donutData: { occupied: 68, free: 32 },
        floorplanBreakdown: {
          openOffice: 65,
          meetingRoom: 20,
          lobby: 11,
          conferenceRoom: 4
        },
        spaceUtilization: 45
      },
      operational: {
        energyAnalytics: 67,
        changePercent: -8.22,
        dynamicOccupancy: 68,
        utilizationAnalytics: 68,
        spaceEfficiency: {
          openOffice: 31,
          meetingRoom: 20,
          lobby: 39,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 29, red: 18, yellow: 15 }
      }
    },
    {
      month: "2024-08",
      displayMonth: formatMonthDisplay(new Date(2024, 7, 31)),
      date: "2024-08-31",
      energy: {
        intensity: 53.0,
        changePercent: -1.85,
        totalKwh: 530000,
        totalCost: 148400,
        nextMonthPrediction: 131600,
        topAppliancesShare: 73.8,
        comparison: { previous: 54.0, current: 53.0 },
        activeSpaces: {
          openOffice: 68,
          meetingRoom: 18,
          lobby: 10,
          conferenceRoom: 4
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [31, 37, 44, 49, 54, 59, 63, 68, 56, 44, 29, 19],
          gas: [3, 4, 5, 2, 4, 5, 3, 4, 3, 6, 8, 7]
        }
      },
      carbon: {
        totalEmissions: 1590,
        changePercent: -1.85,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 11.2,
            changePercent: -2.6,
            emissions: 178
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 26.8,
            changePercent: -0.7,
            emissions: 426
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 27.2,
            changePercent: 0.7,
            emissions: 432
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 34.8,
            changePercent: 0.9,
            emissions: 553
          }
        ],
        gaugeValue: 80,
        timeline: { current: 1590, projected: 1530 }
      },
      esg: {
        overallScore: 80,
        changePercent: 1.27,
        gaugeValue: 80,
        spaceAllocation: {
          lobby: 50,
          conferenceRoom: 11,
          meetingRoom: 15,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 65,
        changePercent: -4.41,
        peopleOnsite: 130,
        totalCapacity: 200,
        donutData: { occupied: 65, free: 35 },
        floorplanBreakdown: {
          openOffice: 64,
          meetingRoom: 20,
          lobby: 11,
          conferenceRoom: 5
        },
        spaceUtilization: 42
      },
      operational: {
        energyAnalytics: 68,
        changePercent: 1.49,
        dynamicOccupancy: 65,
        utilizationAnalytics: 65,
        spaceEfficiency: {
          openOffice: 30,
          meetingRoom: 20,
          lobby: 40,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 28, red: 17, yellow: 14 }
      }
    },
    {
      month: "2024-09",
      displayMonth: formatMonthDisplay(new Date(2024, 8, 30)),
      date: "2024-09-30",
      energy: {
        intensity: 47.0,
        changePercent: -11.32,
        totalKwh: 470000,
        totalCost: 131600,
        nextMonthPrediction: 126000,
        topAppliancesShare: 69.8,
        comparison: { previous: 53.0, current: 47.0 },
        activeSpaces: {
          openOffice: 67,
          meetingRoom: 18,
          lobby: 11,
          conferenceRoom: 4
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [24, 28, 34, 38, 42, 46, 50, 55, 43, 33, 21, 13],
          gas: [6, 8, 10, 5, 9, 11, 7, 9, 7, 14, 18, 16]
        }
      },
      carbon: {
        totalEmissions: 1410,
        changePercent: -11.32,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 10.5,
            changePercent: -6.3,
            emissions: 148
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 26.0,
            changePercent: -3.0,
            emissions: 367
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 27.8,
            changePercent: 2.2,
            emissions: 392
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 35.7,
            changePercent: 2.6,
            emissions: 504
          }
        ],
        gaugeValue: 75,
        timeline: { current: 1410, projected: 1395 }
      },
      esg: {
        overallScore: 82,
        changePercent: 2.5,
        gaugeValue: 82,
        spaceAllocation: {
          lobby: 50,
          conferenceRoom: 10,
          meetingRoom: 16,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 75,
        changePercent: 15.38,
        peopleOnsite: 150,
        totalCapacity: 200,
        donutData: { occupied: 75, free: 25 },
        floorplanBreakdown: {
          openOffice: 67,
          meetingRoom: 18,
          lobby: 11,
          conferenceRoom: 4
        },
        spaceUtilization: 51
      },
      operational: {
        energyAnalytics: 72,
        changePercent: 5.88,
        dynamicOccupancy: 75,
        utilizationAnalytics: 75,
        spaceEfficiency: {
          openOffice: 33,
          meetingRoom: 19,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 32, red: 21, yellow: 18 }
      }
    },
    {
      month: "2024-10",
      displayMonth: formatMonthDisplay(new Date(2024, 9, 31)),
      date: "2024-10-31",
      energy: {
        intensity: 45.0,
        changePercent: -4.26,
        totalKwh: 450000,
        totalCost: 126000,
        nextMonthPrediction: 134400,
        topAppliancesShare: 68.5,
        comparison: { previous: 47.0, current: 45.0 },
        activeSpaces: {
          openOffice: 66,
          meetingRoom: 19,
          lobby: 11,
          conferenceRoom: 4
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [22, 26, 32, 36, 40, 44, 48, 52, 41, 31, 19, 11],
          gas: [8, 10, 13, 6, 11, 13, 8, 10, 8, 16, 20, 18]
        }
      },
      carbon: {
        totalEmissions: 1350,
        changePercent: -4.26,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 10.0,
            changePercent: -4.8,
            emissions: 135
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 25.5,
            changePercent: -1.9,
            emissions: 344
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 28.3,
            changePercent: 1.8,
            emissions: 382
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 36.2,
            changePercent: 1.4,
            emissions: 489
          }
        ],
        gaugeValue: 73,
        timeline: { current: 1350, projected: 1440 }
      },
      esg: {
        overallScore: 83,
        changePercent: 1.22,
        gaugeValue: 83,
        spaceAllocation: {
          lobby: 51,
          conferenceRoom: 10,
          meetingRoom: 15,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 78,
        changePercent: 4.0,
        peopleOnsite: 156,
        totalCapacity: 200,
        donutData: { occupied: 78, free: 22 },
        floorplanBreakdown: {
          openOffice: 68,
          meetingRoom: 18,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 54
      },
      operational: {
        energyAnalytics: 74,
        changePercent: 2.78,
        dynamicOccupancy: 78,
        utilizationAnalytics: 78,
        spaceEfficiency: {
          openOffice: 33,
          meetingRoom: 19,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 33, red: 22, yellow: 19 }
      }
    },
    {
      month: "2024-11",
      displayMonth: formatMonthDisplay(new Date(2024, 10, 30)),
      date: "2024-11-30",
      energy: {
        intensity: 48.0,
        changePercent: 6.67,
        totalKwh: 480000,
        totalCost: 134400,
        nextMonthPrediction: 140000,
        topAppliancesShare: 70.5,
        comparison: { previous: 45.0, current: 48.0 },
        activeSpaces: {
          openOffice: 65,
          meetingRoom: 19,
          lobby: 11,
          conferenceRoom: 5
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [26, 30, 36, 40, 44, 48, 52, 56, 44, 34, 22, 15],
          gas: [12, 15, 18, 9, 16, 18, 12, 15, 12, 22, 26, 24]
        }
      },
      carbon: {
        totalEmissions: 1440,
        changePercent: 6.67,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 9.5,
            changePercent: -5.0,
            emissions: 137
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 25.0,
            changePercent: -2.0,
            emissions: 360
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 28.8,
            changePercent: 1.8,
            emissions: 415
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 36.7,
            changePercent: 1.4,
            emissions: 528
          }
        ],
        gaugeValue: 74,
        timeline: { current: 1440, projected: 1500 }
      },
      esg: {
        overallScore: 84,
        changePercent: 1.2,
        gaugeValue: 84,
        spaceAllocation: {
          lobby: 51,
          conferenceRoom: 10,
          meetingRoom: 16,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 80,
        changePercent: 2.56,
        peopleOnsite: 160,
        totalCapacity: 200,
        donutData: { occupied: 80, free: 20 },
        floorplanBreakdown: {
          openOffice: 69,
          meetingRoom: 17,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 56
      },
      operational: {
        energyAnalytics: 73,
        changePercent: -1.35,
        dynamicOccupancy: 80,
        utilizationAnalytics: 80,
        spaceEfficiency: {
          openOffice: 34,
          meetingRoom: 18,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 34, red: 23, yellow: 20 }
      }
    },
    {
      month: "2024-12",
      displayMonth: formatMonthDisplay(new Date(2024, 11, 31)),
      date: "2024-12-31",
      energy: {
        intensity: 50.0,
        changePercent: 4.17,
        totalKwh: 500000,
        totalCost: 140000,
        nextMonthPrediction: 138600,
        topAppliancesShare: 71.5,
        comparison: { previous: 48.0, current: 50.0 },
        activeSpaces: {
          openOffice: 64,
          meetingRoom: 20,
          lobby: 11,
          conferenceRoom: 5
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [28, 32, 38, 42, 46, 50, 54, 58, 46, 35, 24, 16],
          gas: [14, 17, 20, 10, 18, 20, 14, 17, 14, 26, 30, 28]
        }
      },
      carbon: {
        totalEmissions: 1500,
        changePercent: 4.17,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 9.0,
            changePercent: -5.3,
            emissions: 135
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 24.5,
            changePercent: -2.0,
            emissions: 368
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.3,
            changePercent: 1.7,
            emissions: 440
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 37.2,
            changePercent: 1.4,
            emissions: 558
          }
        ],
        gaugeValue: 76,
        timeline: { current: 1500, projected: 1485 }
      },
      esg: {
        overallScore: 85,
        changePercent: 1.19,
        gaugeValue: 85,
        spaceAllocation: {
          lobby: 52,
          conferenceRoom: 10,
          meetingRoom: 15,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 76,
        changePercent: -5.0,
        peopleOnsite: 152,
        totalCapacity: 200,
        donutData: { occupied: 76, free: 24 },
        floorplanBreakdown: {
          openOffice: 67,
          meetingRoom: 18,
          lobby: 11,
          conferenceRoom: 4
        },
        spaceUtilization: 52
      },
      operational: {
        energyAnalytics: 72,
        changePercent: -1.37,
        dynamicOccupancy: 76,
        utilizationAnalytics: 76,
        spaceEfficiency: {
          openOffice: 33,
          meetingRoom: 19,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 32, red: 21, yellow: 18 }
      }
    },
    {
      month: "2025-01",
      displayMonth: formatMonthDisplay(new Date(2025, 0, 31)),
      date: "2025-01-31",
      energy: {
        intensity: 49.5,
        changePercent: -1.0,
        totalKwh: 495000,
        totalCost: 138600,
        nextMonthPrediction: 134400,
        topAppliancesShare: 71.0,
        comparison: { previous: 50.0, current: 49.5 },
        activeSpaces: {
          openOffice: 64,
          meetingRoom: 20,
          lobby: 10,
          conferenceRoom: 6
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [27, 31, 37, 41, 45, 49, 53, 57, 45, 34, 23, 15],
          gas: [13, 16, 19, 9, 17, 19, 13, 16, 13, 25, 29, 27]
        }
      },
      carbon: {
        totalEmissions: 1485,
        changePercent: -1.0,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.8,
            changePercent: -2.2,
            emissions: 131
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 24.3,
            changePercent: -0.8,
            emissions: 361
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.4,
            changePercent: 0.3,
            emissions: 437
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 37.5,
            changePercent: 0.8,
            emissions: 557
          }
        ],
        gaugeValue: 75,
        timeline: { current: 1485, projected: 1440 }
      },
      esg: {
        overallScore: 85.5,
        changePercent: 0.59,
        gaugeValue: 85.5,
        spaceAllocation: {
          lobby: 52,
          conferenceRoom: 9,
          meetingRoom: 16,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 74,
        changePercent: -2.63,
        peopleOnsite: 148,
        totalCapacity: 200,
        donutData: { occupied: 74, free: 26 },
        floorplanBreakdown: {
          openOffice: 66,
          meetingRoom: 19,
          lobby: 11,
          conferenceRoom: 4
        },
        spaceUtilization: 50
      },
      operational: {
        energyAnalytics: 73,
        changePercent: 1.39,
        dynamicOccupancy: 74,
        utilizationAnalytics: 74,
        spaceEfficiency: {
          openOffice: 32,
          meetingRoom: 19,
          lobby: 39,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 31, red: 20, yellow: 17 }
      }
    },
    {
      month: "2025-02",
      displayMonth: formatMonthDisplay(new Date(2025, 1, 28)),
      date: "2025-02-28",
      energy: {
        intensity: 48.0,
        changePercent: -3.03,
        totalKwh: 480000,
        totalCost: 134400,
        nextMonthPrediction: 126000,
        topAppliancesShare: 69.8,
        comparison: { previous: 49.5, current: 48.0 },
        activeSpaces: {
          openOffice: 63,
          meetingRoom: 20,
          lobby: 11,
          conferenceRoom: 6
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [25, 29, 35, 39, 43, 47, 51, 55, 43, 32, 21, 13],
          gas: [11, 14, 17, 8, 15, 17, 11, 14, 11, 23, 27, 25]
        }
      },
      carbon: {
        totalEmissions: 1440,
        changePercent: -3.03,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.5,
            changePercent: -3.4,
            emissions: 122
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 24.0,
            changePercent: -1.2,
            emissions: 346
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.6,
            changePercent: 0.7,
            emissions: 426
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 37.9,
            changePercent: 1.1,
            emissions: 546
          }
        ],
        gaugeValue: 74,
        timeline: { current: 1440, projected: 1350 }
      },
      esg: {
        overallScore: 86.0,
        changePercent: 0.58,
        gaugeValue: 86.0,
        spaceAllocation: {
          lobby: 52,
          conferenceRoom: 9,
          meetingRoom: 15,
          openOffice: 24
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 76,
        changePercent: 2.7,
        peopleOnsite: 152,
        totalCapacity: 200,
        donutData: { occupied: 76, free: 24 },
        floorplanBreakdown: {
          openOffice: 67,
          meetingRoom: 18,
          lobby: 11,
          conferenceRoom: 4
        },
        spaceUtilization: 52
      },
      operational: {
        energyAnalytics: 74,
        changePercent: 1.37,
        dynamicOccupancy: 76,
        utilizationAnalytics: 76,
        spaceEfficiency: {
          openOffice: 33,
          meetingRoom: 19,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 32, red: 21, yellow: 18 }
      }
    },
    {
      month: "2025-03",
      displayMonth: formatMonthDisplay(new Date(2025, 2, 31)),
      date: "2025-03-31",
      energy: {
        intensity: 45.0,
        changePercent: -6.25,
        totalKwh: 450000,
        totalCost: 126000,
        nextMonthPrediction: 114800,
        topAppliancesShare: 67.5,
        comparison: { previous: 48.0, current: 45.0 },
        activeSpaces: {
          openOffice: 63,
          meetingRoom: 21,
          lobby: 10,
          conferenceRoom: 6
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [22, 26, 32, 36, 40, 44, 48, 52, 40, 30, 18, 11],
          gas: [9, 12, 15, 7, 13, 15, 9, 12, 9, 20, 24, 22]
        }
      },
      carbon: {
        totalEmissions: 1350,
        changePercent: -6.25,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.2,
            changePercent: -3.5,
            emissions: 111
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 23.8,
            changePercent: -0.8,
            emissions: 321
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.8,
            changePercent: 0.7,
            emissions: 402
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.2,
            changePercent: 0.8,
            emissions: 516
          }
        ],
        gaugeValue: 72,
        timeline: { current: 1350, projected: 1230 }
      },
      esg: {
        overallScore: 86.5,
        changePercent: 0.58,
        gaugeValue: 86.5,
        spaceAllocation: {
          lobby: 53,
          conferenceRoom: 9,
          meetingRoom: 15,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 79,
        changePercent: 3.95,
        peopleOnsite: 158,
        totalCapacity: 200,
        donutData: { occupied: 79, free: 21 },
        floorplanBreakdown: {
          openOffice: 68,
          meetingRoom: 18,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 55
      },
      operational: {
        energyAnalytics: 76,
        changePercent: 2.7,
        dynamicOccupancy: 79,
        utilizationAnalytics: 79,
        spaceEfficiency: {
          openOffice: 34,
          meetingRoom: 18,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 34, red: 23, yellow: 20 }
      }
    },
    {
      month: "2025-04",
      displayMonth: formatMonthDisplay(new Date(2025, 3, 30)),
      date: "2025-04-30",
      energy: {
        intensity: 41.0,
        changePercent: -8.89,
        totalKwh: 410000,
        totalCost: 114800,
        nextMonthPrediction: 109200,
        topAppliancesShare: 65.0,
        comparison: { previous: 45.0, current: 41.0 },
        activeSpaces: {
          openOffice: 62,
          meetingRoom: 21,
          lobby: 11,
          conferenceRoom: 6
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [19, 23, 29, 33, 37, 41, 45, 49, 37, 27, 15, 8],
          gas: [6, 8, 11, 5, 10, 12, 7, 9, 7, 16, 20, 18]
        }
      },
      carbon: {
        totalEmissions: 1230,
        changePercent: -8.89,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.0,
            changePercent: -2.4,
            emissions: 98
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 23.5,
            changePercent: -1.3,
            emissions: 289
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 30.0,
            changePercent: 0.7,
            emissions: 369
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.5,
            changePercent: 0.8,
            emissions: 474
          }
        ],
        gaugeValue: 69,
        timeline: { current: 1230, projected: 1170 }
      },
      esg: {
        overallScore: 87.0,
        changePercent: 0.58,
        gaugeValue: 87.0,
        spaceAllocation: {
          lobby: 53,
          conferenceRoom: 9,
          meetingRoom: 16,
          openOffice: 22
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 81,
        changePercent: 2.53,
        peopleOnsite: 162,
        totalCapacity: 200,
        donutData: { occupied: 81, free: 19 },
        floorplanBreakdown: {
          openOffice: 69,
          meetingRoom: 17,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 57
      },
      operational: {
        energyAnalytics: 79,
        changePercent: 3.95,
        dynamicOccupancy: 81,
        utilizationAnalytics: 81,
        spaceEfficiency: {
          openOffice: 35,
          meetingRoom: 17,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 36, red: 25, yellow: 22 }
      }
    },
    {
      month: "2025-05",
      displayMonth: formatMonthDisplay(new Date(2025, 4, 31)),
      date: "2025-05-31",
      energy: {
        intensity: 39.0,
        changePercent: -4.88,
        totalKwh: 390000,
        totalCost: 109200,
        nextMonthPrediction: 120400,
        topAppliancesShare: 63.5,
        comparison: { previous: 41.0, current: 39.0 },
        activeSpaces: {
          openOffice: 62,
          meetingRoom: 21,
          lobby: 10,
          conferenceRoom: 7
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [17, 21, 27, 31, 35, 39, 43, 47, 35, 25, 13, 6],
          gas: [4, 6, 8, 3, 7, 9, 5, 7, 5, 13, 16, 14]
        }
      },
      carbon: {
        totalEmissions: 1170,
        changePercent: -4.88,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 7.8,
            changePercent: -2.5,
            emissions: 91
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 23.2,
            changePercent: -1.3,
            emissions: 271
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 30.2,
            changePercent: 0.7,
            emissions: 353
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.8,
            changePercent: 0.8,
            emissions: 454
          }
        ],
        gaugeValue: 67,
        timeline: { current: 1170, projected: 1290 }
      },
      esg: {
        overallScore: 87.5,
        changePercent: 0.57,
        gaugeValue: 87.5,
        spaceAllocation: {
          lobby: 54,
          conferenceRoom: 8,
          meetingRoom: 15,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 82,
        changePercent: 1.23,
        peopleOnsite: 164,
        totalCapacity: 200,
        donutData: { occupied: 82, free: 18 },
        floorplanBreakdown: {
          openOffice: 70,
          meetingRoom: 17,
          lobby: 9,
          conferenceRoom: 4
        },
        spaceUtilization: 58
      },
      operational: {
        energyAnalytics: 81,
        changePercent: 2.53,
        dynamicOccupancy: 82,
        utilizationAnalytics: 82,
        spaceEfficiency: {
          openOffice: 35,
          meetingRoom: 17,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 37, red: 26, yellow: 23 }
      }
    },
    {
      month: "2025-06",
      displayMonth: formatMonthDisplay(new Date(2025, 5, 30)),
      date: "2025-06-30",
      energy: {
        intensity: 43.0,
        changePercent: 10.26,
        totalKwh: 430000,
        totalCost: 120400,
        nextMonthPrediction: 140000,
        topAppliancesShare: 66.5,
        comparison: { previous: 39.0, current: 43.0 },
        activeSpaces: {
          openOffice: 62,
          meetingRoom: 21,
          lobby: 11,
          conferenceRoom: 6
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [22, 26, 32, 36, 41, 46, 51, 56, 43, 31, 18, 10],
          gas: [3, 4, 5, 2, 4, 5, 3, 4, 3, 7, 9, 8]
        }
      },
      carbon: {
        totalEmissions: 1290,
        changePercent: 10.26,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.0,
            changePercent: 2.6,
            emissions: 103
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 23.5,
            changePercent: 1.3,
            emissions: 303
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 30.0,
            changePercent: -0.7,
            emissions: 387
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.5,
            changePercent: -0.8,
            emissions: 497
          }
        ],
        gaugeValue: 70,
        timeline: { current: 1290, projected: 1500 }
      },
      esg: {
        overallScore: 86.5,
        changePercent: -1.14,
        gaugeValue: 86.5,
        spaceAllocation: {
          lobby: 53,
          conferenceRoom: 9,
          meetingRoom: 15,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 79,
        changePercent: -3.66,
        peopleOnsite: 158,
        totalCapacity: 200,
        donutData: { occupied: 79, free: 21 },
        floorplanBreakdown: {
          openOffice: 68,
          meetingRoom: 18,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 55
      },
      operational: {
        energyAnalytics: 78,
        changePercent: -3.7,
        dynamicOccupancy: 79,
        utilizationAnalytics: 79,
        spaceEfficiency: {
          openOffice: 34,
          meetingRoom: 18,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 35, red: 24, yellow: 21 }
      }
    },
    {
      month: "2025-07",
      displayMonth: formatMonthDisplay(new Date(2025, 6, 31)),
      date: "2025-07-31",
      energy: {
        intensity: 50.0,
        changePercent: 16.28,
        totalKwh: 500000,
        totalCost: 140000,
        nextMonthPrediction: 137200,
        topAppliancesShare: 72.0,
        comparison: { previous: 43.0, current: 50.0 },
        activeSpaces: {
          openOffice: 63,
          meetingRoom: 21,
          lobby: 10,
          conferenceRoom: 6
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [28, 34, 41, 46, 52, 58, 63, 68, 54, 41, 26, 16],
          gas: [2, 3, 4, 1, 3, 4, 2, 3, 2, 5, 7, 6]
        }
      },
      carbon: {
        totalEmissions: 1500,
        changePercent: 16.28,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.5,
            changePercent: 6.3,
            emissions: 128
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 24.0,
            changePercent: 2.1,
            emissions: 360
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.5,
            changePercent: -1.7,
            emissions: 443
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.0,
            changePercent: -1.3,
            emissions: 570
          }
        ],
        gaugeValue: 75,
        timeline: { current: 1500, projected: 1470 }
      },
      esg: {
        overallScore: 85.0,
        changePercent: -1.73,
        gaugeValue: 85.0,
        spaceAllocation: {
          lobby: 52,
          conferenceRoom: 9,
          meetingRoom: 16,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 70,
        changePercent: -11.39,
        peopleOnsite: 140,
        totalCapacity: 200,
        donutData: { occupied: 70, free: 30 },
        floorplanBreakdown: {
          openOffice: 65,
          meetingRoom: 19,
          lobby: 11,
          conferenceRoom: 5
        },
        spaceUtilization: 47
      },
      operational: {
        energyAnalytics: 72,
        changePercent: -7.69,
        dynamicOccupancy: 70,
        utilizationAnalytics: 70,
        spaceEfficiency: {
          openOffice: 32,
          meetingRoom: 19,
          lobby: 39,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 31, red: 20, yellow: 17 }
      }
    },
    {
      month: "2025-08",
      displayMonth: formatMonthDisplay(new Date(2025, 7, 31)),
      date: "2025-08-31",
      energy: {
        intensity: 49.0,
        changePercent: -2.0,
        totalKwh: 490000,
        totalCost: 137200,
        nextMonthPrediction: 123200,
        topAppliancesShare: 71.2,
        comparison: { previous: 50.0, current: 49.0 },
        activeSpaces: {
          openOffice: 62,
          meetingRoom: 21,
          lobby: 11,
          conferenceRoom: 6
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [27, 33, 40, 45, 51, 57, 62, 66, 53, 40, 25, 15],
          gas: [2, 3, 4, 1, 3, 4, 2, 3, 2, 5, 7, 6]
        }
      },
      carbon: {
        totalEmissions: 1470,
        changePercent: -2.0,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.3,
            changePercent: -2.4,
            emissions: 122
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 23.8,
            changePercent: -0.8,
            emissions: 350
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 29.7,
            changePercent: 0.7,
            emissions: 437
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.2,
            changePercent: 0.5,
            emissions: 561
          }
        ],
        gaugeValue: 74,
        timeline: { current: 1470, projected: 1320 }
      },
      esg: {
        overallScore: 86.0,
        changePercent: 1.18,
        gaugeValue: 86.0,
        spaceAllocation: {
          lobby: 53,
          conferenceRoom: 9,
          meetingRoom: 15,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 68,
        changePercent: -2.86,
        peopleOnsite: 136,
        totalCapacity: 200,
        donutData: { occupied: 68, free: 32 },
        floorplanBreakdown: {
          openOffice: 64,
          meetingRoom: 20,
          lobby: 11,
          conferenceRoom: 5
        },
        spaceUtilization: 45
      },
      operational: {
        energyAnalytics: 73,
        changePercent: 1.39,
        dynamicOccupancy: 68,
        utilizationAnalytics: 68,
        spaceEfficiency: {
          openOffice: 31,
          meetingRoom: 20,
          lobby: 39,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 30, red: 19, yellow: 16 }
      }
    },
    {
      month: "2025-09",
      displayMonth: formatMonthDisplay(new Date(2025, 8, 30)),
      date: "2025-09-30",
      energy: {
        intensity: 44.0,
        changePercent: -10.2,
        totalKwh: 440000,
        totalCost: 123200,
        nextMonthPrediction: 210000,
        topAppliancesShare: 67.8,
        comparison: { previous: 49.0, current: 44.0 },
        activeSpaces: {
          openOffice: 62,
          meetingRoom: 21,
          lobby: 10,
          conferenceRoom: 7
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [22, 26, 32, 36, 41, 46, 50, 54, 42, 31, 18, 10],
          gas: [5, 7, 9, 4, 8, 10, 6, 8, 6, 12, 16, 14]
        }
      },
      carbon: {
        totalEmissions: 1320,
        changePercent: -10.2,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 8.0,
            changePercent: -3.6,
            emissions: 106
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 23.5,
            changePercent: -1.3,
            emissions: 310
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 30.0,
            changePercent: 1.0,
            emissions: 396
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.5,
            changePercent: 0.8,
            emissions: 508
          }
        ],
        gaugeValue: 71,
        timeline: { current: 1320, projected: 1290 }
      },
      esg: {
        overallScore: 87.5,
        changePercent: 1.74,
        gaugeValue: 87.5,
        spaceAllocation: {
          lobby: 54,
          conferenceRoom: 8,
          meetingRoom: 15,
          openOffice: 23
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 77,
        changePercent: 13.24,
        peopleOnsite: 154,
        totalCapacity: 200,
        donutData: { occupied: 77, free: 23 },
        floorplanBreakdown: {
          openOffice: 67,
          meetingRoom: 18,
          lobby: 11,
          conferenceRoom: 4
        },
        spaceUtilization: 53
      },
      operational: {
        energyAnalytics: 77,
        changePercent: 5.48,
        dynamicOccupancy: 77,
        utilizationAnalytics: 77,
        spaceEfficiency: {
          openOffice: 33,
          meetingRoom: 19,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 34, red: 23, yellow: 20 }
      }
    },
    {
      month: "2025-10",
      displayMonth: formatMonthDisplay(new Date(2025, 9, 14)),
      date: "2025-10-14",
      isPartial: true,
      daysInMonth: 14,
      energy: {
        intensity: 75.0,
        changePercent: 70.45,
        totalKwh: 750000,
        totalCost: 210000,
        nextMonthPrediction: 224000,
        topAppliancesShare: 74.5,
        comparison: {
          previous: 44.0,
          current: 75.0
        },
        activeSpaces: {
          openOffice: 61,
          meetingRoom: 20,
          lobby: 11,
          conferenceRoom: 8
        },
        hourlyUsage: {
          hours: [
            "8:00",
            "9:00",
            "10:00",
            "11:00",
            "12:00",
            "13:00",
            "14:00",
            "15:00",
            "16:00",
            "17:00",
            "18:00",
            "19:00"
          ],
          electricity: [35, 42, 50, 58, 65, 72, 78, 85, 68, 52, 35, 25],
          gas: [12, 15, 20, 10, 18, 22, 14, 17, 14, 28, 35, 30]
        }
      },
      carbon: {
        totalEmissions: 2250,
        changePercent: 70.45,
        floorBreakdown: [
          {
            floor: isGerman ? "Etage 1" : "Floor 1",
            percentage: 7.9,
            changePercent: -1.3,
            emissions: 178
          },
          {
            floor: isGerman ? "Etage 2" : "Floor 2",
            percentage: 23.3,
            changePercent: -0.9,
            emissions: 524
          },
          {
            floor: isGerman ? "Etage 3" : "Floor 3",
            percentage: 30.1,
            changePercent: 0.3,
            emissions: 677
          },
          {
            floor: isGerman ? "Etage 4" : "Floor 4",
            percentage: 38.7,
            changePercent: 0.5,
            emissions: 871
          }
        ],
        gaugeValue: 82,
        timeline: { current: 2250, projected: 2100 }
      },
      esg: {
        overallScore: 82.0,
        changePercent: -6.29,
        gaugeValue: 82.0,
        spaceAllocation: {
          lobby: 54,
          conferenceRoom: 8,
          meetingRoom: 16,
          openOffice: 22
        },
        compliance: {
          co2Reporting: true,
          energyTracking: true,
          gresbIntegration: true,
          iso14001: true
        }
      },
      occupancy: {
        rate: 80,
        changePercent: 3.9,
        peopleOnsite: 160,
        totalCapacity: 200,
        donutData: { occupied: 80, free: 20 },
        floorplanBreakdown: {
          openOffice: 69,
          meetingRoom: 17,
          lobby: 10,
          conferenceRoom: 4
        },
        spaceUtilization: 56
      },
      operational: {
        energyAnalytics: 65,
        changePercent: -15.58,
        dynamicOccupancy: 80,
        utilizationAnalytics: 80,
        spaceEfficiency: {
          openOffice: 34,
          meetingRoom: 18,
          lobby: 38,
          conferenceRoom: 10
        },
        costPerSqm: { blue: 42, red: 32, yellow: 28 }
      }
    }
  ],

  getCurrentMonthData() {
    return this.monthlyData[this.monthlyData.length - 1];
  },

  getDataByMonth(monthString) {
    return this.monthlyData.find((m) => m.month === monthString);
  },

  getDateRange() {
    return {
      start: this.monthlyData[0].date,
      end: this.monthlyData[this.monthlyData.length - 1].date
    };
  }
};

export function interpolatePartialMonth(lastMonthData, currentDate) {
  const lastDataDate = new Date(lastMonthData.date);
  const daysSinceLastUpdate = Math.floor(
    (currentDate - lastDataDate) / (1000 * 60 * 60 * 24)
  );

  if (daysSinceLastUpdate === 0) {
    return lastMonthData;
  }

  const daysInCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();
  const progressPercent = currentDate.getDate() / daysInCurrentMonth;

  const interpolatedData = {
    ...lastMonthData,
    date: currentDate.toISOString().split("T")[0],
    displayMonth: formatMonthDisplay(currentDate),
    daysInMonth: currentDate.getDate(),
    energy: {
      ...lastMonthData.energy,
      intensity:
        lastMonthData.energy.intensity +
        (lastMonthData.energy.changePercent / 100) *
          lastMonthData.energy.intensity *
          progressPercent,
      totalKwh: Math.round(lastMonthData.energy.totalKwh * progressPercent),
      totalCost: Math.round(lastMonthData.energy.totalCost * progressPercent)
    }
  };

  return interpolatedData;
}

export function getRealEstateData() {
  const data = { ...realEstateData };
  const monthlyDataCopy = [...realEstateData.monthlyData];

  const lastStaticEntry = monthlyDataCopy[monthlyDataCopy.length - 1];
  const lastStaticDate = new Date(lastStaticEntry.date);
  const today = new Date();

  if (today > lastStaticDate) {
    const updatedData = interpolatePartialMonth(lastStaticEntry, today);

    if (lastStaticEntry.month === updatedData.month) {
      monthlyDataCopy[monthlyDataCopy.length - 1] = updatedData;
    } else {
      monthlyDataCopy.push(updatedData);
    }
  }

  data.monthlyData = monthlyDataCopy;
  return data;
}
